import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './BookingFlow.css';
import { addTeamToCart, getMyCart, updateCartQuantity } from '../../../../utils/APIs/servicesApis';
import { updateCartEventType } from '../../../../utils/APIs/cartApis';
import { toast } from 'react-toastify';
import {
    getCachedHourlyEventType,
    HOURLY_CART_CATEGORY_EVENT,
    setCachedHourlyEventType,
} from '../hourlyCartCategory';

const BookingFlow = ({ pricing }) => {
    const navigate = useNavigate();
    const [selectedPlanId, setSelectedPlanId] = useState(pricing?.plans?.length > 0 ? pricing.plans[0].id : null);
    const [cartData, setCartData] = useState(null);
    const [loadingCart, setLoadingCart] = useState(true);
    const [selectedEventType, setSelectedEventType] = useState(
        () => getCachedHourlyEventType()
    );

    const fetchCart = async () => {
        try {
            const response = await getMyCart();
            if (response.data.success) {
                const data = response.data.data;
                setCartData(data);
                const eventType = data?.eventType || getCachedHourlyEventType();
                if (eventType) {
                    setSelectedEventType(eventType);
                    setCachedHourlyEventType(eventType);
                }
            }
        } catch (error) {
            console.error("Error fetching cart:", error);
        } finally {
            setLoadingCart(false);
        }
    };

    const resolveEventTypeForCart = async () => {
        const cached = getCachedHourlyEventType() || selectedEventType;
        try {
            const response = await getMyCart();
            const cartEventType = response?.data?.data?.eventType;
            if (cartEventType && String(cartEventType).trim()) {
                return String(cartEventType).trim();
            }
            if (cached && String(cached).trim()) {
                await updateCartEventType(cached);
                setCachedHourlyEventType(cached);
                return String(cached).trim();
            }
        } catch (error) {
            console.error("Error resolving cart category:", error);
            if (cached && String(cached).trim()) {
                return String(cached).trim();
            }
        }
        return "";
    };

    useEffect(() => {
        fetchCart();

        const onCategoryUpdated = (event) => {
            const eventType = event?.detail?.eventType || "";
            setSelectedEventType(eventType);
            if (eventType) {
                setCachedHourlyEventType(eventType);
            }
        };

        window.addEventListener(HOURLY_CART_CATEGORY_EVENT, onCategoryUpdated);
        return () => {
            window.removeEventListener(HOURLY_CART_CATEGORY_EVENT, onCategoryUpdated);
        };
    }, []);

    if (!pricing || !pricing.plans || pricing.plans.length === 0) {
        return <div className="bf-error">No plans available at the moment.</div>;
    }

    const handleSelectPlan = (planId) => {
        setSelectedPlanId(planId);
    };

    const handleAddMember = async (member) => {
        try {
            const durationValue = Number(selectedPlanId);
            const selectedPlan = pricing.plans.find(p => p.id === selectedPlanId);
            const durationText = selectedPlan?.duration;

            // Check if this specific member for this duration is already in the cart
            const existingItem = cartItems.find(item => {
                const itemDurationText = item.name.match(/\(([^)]+)\)/)?.[1];
                return item.planId === member.id && itemDurationText === durationText;
            });

            if (existingItem) {
                // If exists, just increase quantity
                await handleIncreaseQuantity(existingItem._id);
            } else {
                // If not, add new
                const selection = {
                    planId: member.id, // Role ID
                    durationValue: durationValue,
                    selectedroleId: null,
                };

                if (member.pricingType === 'duration_based' && member.originalData) {
                    const option = member.originalData.pricingOptions.find(
                        opt => opt.durationValue === selection.durationValue
                    );
                    if (option) {
                        selection.selectedroleId = option._id;
                    }
                }

                await addTeamToCart([selection]);
                await fetchCart(); // Refresh cart from server
            }
        } catch (error) {
            console.error("Error adding to cart API:", error);
        }
    };

    const handleIncreaseQuantity = async (itemId) => {
        try {
            await updateCartQuantity(itemId, 'increase');
            await fetchCart();
        } catch (error) {
            console.error("Error increasing quantity:", error);
        }
    };

    const handleRemoveMember = async (itemId) => {
        try {
            await updateCartQuantity(itemId, 'decrease');
            await fetchCart();
        } catch (error) {
            console.error("Error decreasing quantity:", error);
        }
    };

    const cartItems = (cartData?.items || []).filter(item => item.category?.toLowerCase() === 'shoot_team');

    // Group cart items by Duration
    const groupedCartItems = cartItems.reduce((acc, item) => {
        // Extract duration text from name, e.g. "Photographer (3 Hours)" -> "3 Hours"
        const durationText = item.name.match(/\(([^)]+)\)/)?.[1] || 'Standard';

        if (!acc[durationText]) {
            acc[durationText] = {
                duration: durationText,
                items: []
            };
        }
        acc[durationText].items.push({
            ...item,
            cartItemId: item._id,
            title: item.name.includes(' (') ? item.name.split(' (')[0] : item.name
        });
        return acc;
    }, {});

    const totalPrice = cartItems.reduce((acc, item) => acc + (item.price * (item.quantity || 1)), 0);

    return (
        <div className="bf-container">
            <h2 className="bf-section-title">Select Your Duration</h2>
            <div className="bf-plans-grid">
                {pricing.plans.map(plan => (
                    <div
                        key={plan.id}
                        className={`bf-plan-card ${selectedPlanId === plan.id ? 'selected' : ''}`}
                        onClick={() => handleSelectPlan(plan.id)}
                    >
                        <h3 className="bf-plan-duration">{plan.duration}</h3>
                        <p className="bf-plan-subtitle">{plan.subtitle}</p>
                        <div className="bf-plan-price-btn">
                            Starting at ₹ {plan.price.toLocaleString('en-IN')}/-
                        </div>
                    </div>
                ))}
            </div>

            <h2 className="bf-section-title">Build Your Own Team</h2>
            <div className="bf-content-wrapper">
                <div className="bf-team-list">
                    {pricing.teamMembers.map(member => (
                        <div key={member.id} className="bf-team-member">
                            <div className="bf-member-info">
                                <h3 className="bf-member-title">
                                    {member.title}
                                    {member.subtitle && <span className="bf-member-subtitle"> {member.subtitle}</span>}
                                </h3>
                                <ul className="bf-member-features">
                                    {member.features.map((feature, index) => (
                                        <li key={index}>• {feature}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bf-member-actionBox">
                                <div className="bf-member-image-wrapper">
                                    <img
                                        src={`/asset/ServicePages/HourlyPages/${member.image}`}
                                        alt={member.title}
                                        className="bf-member-image"
                                    />
                                    <button
                                        className="bf-add-btn"
                                        onClick={() => handleAddMember(member)}
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bf-cart-container">
                    <div className="bf-cart">
                        <div className="bf-cart-header">
                            <span className="bf-cart-icon">🛒</span>
                            <h3>Cart</h3>
                        </div>
                        <div className="bf-cart-items">
                            {loadingCart ? (
                                <div className="bf-cart-loading">Loading Cart...</div>
                            ) : cartItems.length === 0 ? (
                                <div className="bf-cart-empty">Your team is empty.<br />Add members to start.</div>
                            ) : (
                                Object.keys(groupedCartItems).map(durationKey => (
                                    <div key={durationKey} className="bf-cart-group">
                                        <h4 className="bf-cart-group-title">{groupedCartItems[durationKey].duration}</h4>
                                        <div className="bf-cart-group-items">
                                            {groupedCartItems[durationKey].items.map(item => (
                                                <div key={item.cartItemId} className="bf-cart-item">
                                                    <div className="bf-cart-item-name">
                                                        {item.title}
                                                    </div>
                                                    <div className="bf-cart-item-right">
                                                        <div className="bf-cart-item-controls">
                                                            <button className="bf-qty-btn" onClick={() => handleRemoveMember(item.cartItemId)}>-</button>
                                                            <span className="bf-qty-val">{item.quantity}</span>
                                                            <button className="bf-qty-btn" onClick={() => handleIncreaseQuantity(item.cartItemId)}>+</button>
                                                        </div>
                                                        <div className="bf-cart-item-price">₹ {(item.price * item.quantity).toLocaleString('en-IN')}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="bf-cart-total-box">
                            <div className="bf-cart-total-price">
                                ₹ {totalPrice.toLocaleString('en-IN')}
                            </div>
                            <button className="bf-view-cart-btn" onClick={async () => {
                                if (cartItems.length === 0) {
                                    toast.error("Cart is empty. Please add items to proceed.");
                                    return;
                                }
                                try {
                                    const eventType = await resolveEventTypeForCart();
                                    if (!eventType) {
                                        toast.error("Please select a Category before viewing your cart.");
                                        return;
                                    }
                                    navigate('/view-cart');
                                } catch (err) {
                                    console.error("Error checking cart category:", err);
                                    toast.error("Something went wrong. Please try again.");
                                }
                            }}>View Cart</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingFlow;
